import React, { useMemo, useState, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
// import { scaleQuantize } from 'd3-scale';
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import axios from "axios";
import Swal from "sweetalert2";
import { AUTH_URL, API_URL, CHAT_URL } from "../../utils/constants";
// import { truncateText } from '../../utils/utils';
import TruncatedText from "../../utils/TruncatedText";
import { toCamelCase, toSentenceCase } from "../../utils/utils";
import PieCharts from "../dashboard/PieCharts";
import upDownArrow from "../../assets/images/up-down-arrow.svg";
import { getGrievance } from "../adminDashboard/AdminUser";
const GRADIENT_COLORS = ["#FF852B", "#D37929", "#B34700"];

export default function DashboardAdmin({
  user,
  dashboardData,
  data,
  setQueryUpdate,
  overdueData,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [showCommentArea, setShowCommentArea] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [comment, setComment] = useState(""); // New state to store comment
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [atrFile, setAtrFile] = useState(null);
  const [atrUploadLoading, setAtrUploadLoading] = useState(false);
  const [atrSummary, setAtrSummary] = useState("");
  const [atrFileUrl, setAtrFileUrl] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [audioUploadLoading, setAudioUploadLoading] = useState(false);
  const [pieChartData, setPieChartData] = useState(null);
  const [audioSummary, setAudioSummary] = useState("");
  const [audioFileUrl, setAudioFileUrl] = useState("");
  const [response, setResponse] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParam, setSearchParam] = useState("All");
  const [transfer, setTransfer] = useState(false);
  const [transferAdmin, setTransferAdmin] = useState(false);
  const [officerComments, setOfficerComments] = useState("");
  const [tranferDept, setTransferDept] = useState("");

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const calculateDaysLapsed = (row) => {
    if (!row.grievance_submission_timestamp) return 0;
    const submittedDate = new Date(row.grievance_submission_timestamp);
    const date = new Date();
    const diff = date - submittedDate;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (row.tat === "within 1 day") return days - 1 > 0 ? days - 1 : 0;
    if (row.tat === "2 days") return days - 2 > 0 ? days - 2 : 0;
    if (row.tat === "3 days") return days - 3 > 0 ? days - 3 : 0;
    return 0;
  };
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Derive counts and groupings
  const stats = useMemo(() => {
    const normalizeStatus = (status) => {
      const s = (status || "pending").toLowerCase();

      if (s === "pending") return "Pending";
      if (s === "resolved" || s === "closed") return "Resolved";
      if (s === "rejected") return "Rejected";
      if (s === "responded") return "Responded";

      return "Other";
    };

    const statusCounts = {
      Pending: 0,
      Responded: 0,
      Resolved: 0,
      Rejected: 0,
      Other: 0,
    };

    const categories = {};
    const grievances = [];

    data.forEach((item) => {
      const normalizedStatus = normalizeStatus(item.status);

      statusCounts[normalizedStatus] =
        (statusCounts[normalizedStatus] || 0) + 1;

      const category = item.llm_subcategory || "Other";

      categories[category] = (categories[category] || 0) + 1;

      grievances.push({
        id: item.grievance_id.slice(0, 6) + "...",
        category,
        status: normalizedStatus,
        date: new Date(item.grievance_submission_timestamp).toLocaleDateString(
          "en-IN",
        ),
      });
    });

    // Backend percentage summary
    const statusSummary =
      Array.isArray(dashboardData.summary) && dashboardData.summary.length
        ? (() => {
            const aggregated = {};
            dashboardData.summary.forEach((item) => {
              const normalizedStatus = item.name;
              if (!aggregated[normalizedStatus]) {
                aggregated[normalizedStatus] = {
                  name: normalizedStatus,
                  value: 0,
                };
              }
              aggregated[normalizedStatus].value = item.count;
            });
            return Object.values(aggregated).map((item) => ({
              ...item,
            }));
          })()
        : null;

    return {
      total: data.length,

      statusPieData: statusSummary,

      categories: Object.entries(categories).map(([name, value]) => ({
        name,
        value,
      })),

      grievances,

      allGrievances: data,

      overdueGrievances: overdueData,
    };
  }, [data, dashboardData, overdueData]);

  const filteredGrievances = useMemo(() => {
    let filtered = stats.allGrievances;
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        // Filter only by grievance ID as per request
        return (
          item.grievance_id &&
          item.grievance_id.toLowerCase().includes(lowerQuery)
        );
      });
    }

    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case "grievance_id":
            aValue = a.grievance_id || "";
            bValue = b.grievance_id || "";
            break;
          case "severity":
            aValue = a.severity || "";
            bValue = b.severity || "";
            break;
          case "source":
            aValue = a.source || "";
            bValue = b.source || "";
            break;
          case "reg_date":
            aValue = new Date(a.grievance_submission_timestamp || 0).getTime();
            bValue = new Date(b.grievance_submission_timestamp || 0).getTime();
            break;
          case "tat":
            aValue = a.tat || "";
            bValue = b.tat || "";
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [
    stats.allGrievances,
    stats.overdueGrievances,
    searchQuery,
    searchParam,
    sortConfig,
  ]);

  // Fetch pie chart percentages from the new API
  useEffect(() => {
    const fetchPieChartData = async () => {
      try {
        if (data && data.length > 0) {
          const userId = data[0].user_id;
          if (userId) {
            const res = await axios.get(
              `${AUTH_URL}api/pendingrespondedpercentageuser?user_id=${userId}`,
            );
            if (res.data && res.data.success) {
              setPieChartData({
                pending: {
                  count: res.data.pending_count,
                  percentage: res.data.pending_percentage,
                },
                responded: {
                  count: res.data.responded_count,
                  percentage: res.data.responded_percentage,
                },
                total: res.data.total_grievances,
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching pie chart data:", error);
      }
    };

    fetchPieChartData();
  }, [data]);

  const handleViewClick = async (item) => {
    const res = await getGrievance(item.grievance_id);
    if (res) {
      setSelectedRow(res);
      setIsViewOnly(true); // Set view-only mode
      setIsModalOpen(true);
    }
    // setSelectedRow(item);
    // setIsViewOnly(true); // Set view-only mode
    // setIsModalOpen(true);
  };

  const handleEditClick = async (item) => {
    const res = await getGrievance(item.grievance_id);
    if (res) {
      setSelectedRow(res);
      setIsViewOnly(false); // Set view-only mode
      setIsModalOpen(true);
    }
    // setSelectedRow(item);
    // setIsViewOnly(false); // Set view-only mode
    // setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
    setAtrFile(null);
    setAtrSummary("");
    setAtrFileUrl("");
    setAudioFile(null);
    setAudioSummary("");
    setAudioFileUrl("");
  };

  const handleTransfer = () => {
    setTransfer(true);
  };
  const handleTransferAdmin = () => {
    setTransferDept("Admin");
    setTransferAdmin(true);
  };
  const submitTransfer = async (e) => {
    e.preventDefault();
    const payload = {
      query_id: selectedRow._id,
      officer_comments: officerComments,
      transfer_to: tranferDept,
    };

    let res = await axios.post(`${AUTH_URL}api/transfer_grievance`, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (res.status === 200) {
      // Trigger notification to the new department/officer
      try {
        await axios.put(
          `${API_URL}api/admin_sendnotification_officer`,
          {
            officer_name: tranferDept,
            grievance_id: selectedRow.grievance_id,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
      } catch (notifErr) {
        console.error("Failed to send transfer notification:", notifErr);
      }

      Swal.fire({
        title: "Grievance Updated!",
        text: "Grievance Transfered Successfully!",
        icon: "success",
      });
      setTransfer(false);
      setTransferAdmin(false);
      setIsModalOpen(false);

      setOfficerComments("");
      setTransferDept("");
    }
  };
  const closeTransfer = () => {
    setTransfer(false);
    setTransferAdmin(false);
    setOfficerComments("");
    setTransferDept("");
  };
  const setTransferDeptartment = (e) => {
    const { value } = e.target;
    setTransferDept(value);
  };

  const handleOfficeComment = (e) => {
    const { value } = e.target;
    setOfficerComments(value);
  };
  // Helper: The update_atr backend only persists atr_summary, not atr_file_url.
  // We embed the file_url at the end of summary using a separator so it survives round-trips.
  const FILE_URL_SEPARATOR = "|||FILE_URL|||";

  const encodeSummaryWithUrl = (summary, fileUrl) => {
    if (!fileUrl) return summary;
    return `${summary}${FILE_URL_SEPARATOR}${fileUrl}`;
  };

  const decodeSummary = (raw) => {
    if (!raw) return { summary: "", fileUrl: "" };
    const parts = raw.split(FILE_URL_SEPARATOR);
    return { summary: parts[0], fileUrl: parts[1] || "" };
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const isAlreadyResponded =
        selectedRow?.officer_grievance_status?.toLowerCase() !== "add comment";

      if (isAlreadyResponded) {
        let summaryToSave = "";
        let urlToSave = "";
        let url = "";
        let payload = null;
        // If already responded, we are just saving the ATR via update_atr
        if (!selectedRow.atr_uploaded) {
          summaryToSave =
            atrSummary || decodeSummary(selectedRow.atr_summary).summary;
          urlToSave =
            atrFileUrl || decodeSummary(selectedRow.atr_summary).fileUrl;
          url = "update_atr";
          payload = {
            query_id: selectedRow._id,
            summary: encodeSummaryWithUrl(summaryToSave, urlToSave),
            status: "Resolved",
          };
        } else {
          summaryToSave =
            audioSummary ||
            decodeSummary(selectedRow.feedback_call_summary).summary;
          urlToSave =
            audioFileUrl || decodeSummary(selectedRow.atr_summary).summary;
          url = "update_feedback";
          payload = {
            query_id: selectedRow._id,
            summary: encodeSummaryWithUrl(summaryToSave, urlToSave),
            feedback: response,
          };
        }

        let res = await axios.put(`${AUTH_URL}api/${url}`, payload, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (res.status === 200) {
          setQueryUpdate(true);
          setIsModalOpen(false);
          setAtrFile(null);
          setAtrSummary("");
          setAtrFileUrl("");
          if (url === "update_feedback") {
            Swal.fire({
              title: "Audio Saved!",
              text: "Audio document has been saved successfully.",
              icon: "success",
            });
          } else {
            Swal.fire({
              title: "ATR Saved!",
              text: "ATR document has been saved successfully.",
              icon: "success",
            });
          }
        }
      } else {
        // Normal response save for pending grievances
        const comments =
          selectedRow.officer_grievance_comments ||
          selectedRow.grievance_response ||
          "";
        const isResponded = comments.trim().length > 0;

        const payload = {
          query_id: selectedRow._id,
          officer_grievance_comments: comments,
          atr_uploaded: false,
        };

        if (isResponded) {
          payload.status = "Responded";
        }

        let res = await axios.put(
          `${AUTH_URL}api/grievance_response`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        if (res.status === 200) {
          setQueryUpdate(true);
          setIsModalOpen(false);
          setAtrFile(null);
          setAtrSummary("");
          setAtrFileUrl("");
          Swal.fire({
            title: "Query Updated!",
            text: "You have successfully updated.",
            icon: "success",
          });
        }
      }
    } catch (error) {
      console.log("Error in Update Query API", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: `${error?.response?.data?.error || "Error in Update Query API"}`,
      });
    } finally {
      setLoading(false);
      setIsModalOpen(false);
    }
  };

  const handleAtrUpload = async () => {
    if (!atrFile) {
      Swal.fire("Error", "Please select a file to upload", "error");
      return;
    }
    const formData = new FormData();
    formData.append("uploaded_file", atrFile);

    try {
      setAtrUploadLoading(true);
      const res = await axios.post(
        `${CHAT_URL}v1/categorize_atr_file`,
        formData,
        {
          headers: {
            accept: "application/json",
            "x-source": "12",
            "x-consumer-id": "12",
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res.status === 200 && res.data) {
        setAtrSummary(res.data.summary);
        setAtrFileUrl(res.data.file_url);
        Swal.fire({
          title: "Success!",
          text: "ATR Document uploaded and analyzed successfully.",
          icon: "success",
        });
      }
    } catch (error) {
      console.log("Error uploading ATR:", error);
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Could not upload the ATR document.",
      });
    } finally {
      setAtrUploadLoading(false);
    }
  };

  const handleAudioUpload = async () => {
    if (!audioFile) {
      Swal.fire("Error", "Please select an audio file to upload", "error");
      return;
    }
    const formData = new FormData();
    formData.append("uploaded_file", audioFile);

    try {
      setAudioUploadLoading(true);
      const res = await axios.post(`${CHAT_URL}v1/upload_file`, formData, {
        headers: {
          accept: "application/json",
          "x-source": "787",
          "x-consumer-id": "878",
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.status === 200 && res.data) {
        setAudioSummary(res.data.summary);
        setAudioFileUrl(res.data.file_url);
        setResponse(res.data.response);
        Swal.fire({
          title: "Success!",
          text: "Audio file uploaded and analyzed successfully.",
          icon: "success",
        });
      }
    } catch (error) {
      console.log("Error uploading audio:", error);
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Could not upload the audio file.",
      });
    } finally {
      setAudioUploadLoading(false);
    }
  };
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSelectedRow((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleATR = async (grievanceId) => {
    // Get file URL from state (fresh upload) or from persisted summary
    const fileUrl =
      atrFileUrl ||
      decodeSummary(selectedRow?.atr_summary).fileUrl ||
      selectedRow?.atr_file_url;
    if (!fileUrl) {
      Swal.fire({
        icon: "info",
        title: "No ATR File",
        text: "No ATR file URL is available for download.",
      });
      return;
    }

    try {
      // Fetch the file as a blob to force download (cross-origin URLs ignore download attribute)
      // const response = await fetch(fileUrl);
      // const blob = await response.blob();
      // const blobUrl = URL.createObjectURL(blob);
      // const link = document.createElement("a");
      // link.href = blobUrl;
      // link.download = `ATR_${grievanceId || "Document"}.pdf`;
      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);
      // URL.revokeObjectURL(blobUrl);
      window.open(fileUrl, "_blank");
    } catch (error) {
      console.log("Error downloading ATR:", error);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: "Could not download the ATR document.",
      });
    }
  };

  const handleAudioDownload = async () => {
    if (!audioFileUrl) {
      Swal.fire({
        icon: "info",
        title: "No Audio File",
        text: "No audio file URL is available for download.",
      });
      return;
    }

    try {
      const response = await fetch(audioFileUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "audio download.wav";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.log("Error downloading audio:", error);
      Swal.fire({
        icon: "error",
        title: "Download Failed",
        text: "Could not download the audio file.",
      });
    }
  };

  const handleLikeClick = (e) => {
    e.preventDefault();
    setIsLiked(true);
    setShowCommentArea(false); // Hide comment area after liking
  };

  const handleDislikeClick = (e) => {
    e.preventDefault();
    setIsLiked(false);
    setShowCommentArea(true); // Show comment area after disliking
  };

  const handleSave1 = async () => {
    setSaveLoading(true);
    try {
      const feedbackData = {
        grievance_id: selectedRow.id, // Assuming you have selectedRow.id for grievance ID
        feedbackrating: isLiked ? 1 : 0, // 1 for like, 0 for dislike
        feedbacktext: comment, // The comment entered by the user
      };

      const res = await axios(`${AUTH_URL}/api/feedback`, feedbackData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (res.status === 200) {
        console.log("Feedback saved successfully");
        // Optionally, reset the states after saving
        setIsLiked(false);
        setComment("");
        setShowCommentArea(false);
      }
    } catch (error) {
      console.error("Error saving feedback:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen font-sans">
      {/* Summary Boxes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-indigo-600 text-white rounded-2xl p-6 flex justify-between items-center shadow-lg hover:scale-[1.02] transition-transform duration-200 ease-in-out">
          <div>
            <p className="text-sm font-medium text-indigo-100 mb-1">
              Total User Grievances Registered
            </p>
            <p className="text-4xl font-extrabold tracking-tight">
              {stats.total}
            </p>
          </div>
          <div className="bg-indigo-500 p-3 rounded-full">
            <HelpOutlineIcon fontSize="large" className="text-white" />
          </div>
        </div>

        <div className="bg-amber-500 text-white rounded-2xl p-6 flex justify-between items-center shadow-lg hover:scale-[1.02] transition-transform duration-200 ease-in-out">
          <div>
            <p className="text-sm font-medium text-yellow-100 mb-1">
              Number of User Grievances Pending
            </p>
            <p className="text-4xl font-extrabold tracking-tight">
              {dashboardData.pending || 0}
            </p>
          </div>
          <div className="bg-amber-400 p-3 rounded-full">
            <HourglassTopIcon fontSize="large" className="text-white" />
          </div>
        </div>

        <div className="bg-emerald-600 text-white rounded-2xl p-6 flex justify-between items-center shadow-lg hover:scale-[1.02] transition-transform duration-200 ease-in-out">
          <div>
            <p className="text-sm font-medium text-emerald-100 mb-1">
              Number of User Grievances Closed
            </p>
            <p className="text-4xl font-extrabold tracking-tight">
              {dashboardData.closed || 0}
            </p>
          </div>
          <div className="bg-emerald-500 p-3 rounded-full">
            <CheckCircleOutlineIcon fontSize="large" className="text-white" />
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="flex flex-col md:flex-row justify-center h-auto md:h-[50vh] w-full mb-4 gap-4">
        <div className="bg-white shadow rounded-lg p-4 w-full md:w-[50%]">
          <PieCharts
            data={stats.statusPieData}
            nameKey="name"
            title="Grievance Status"
            xAxisName="Number of Queries"
            colorMap={{
              Resolved: "#10B981",
              "Attach ATR": "#F59E0B",
              "Add Comment": "#6366F1",
            }}
            colors={["#6366F1", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6"]}
          />
        </div>

        <div className="bg-white p-6 w-full md:w-[50%] overflow-hidden flex flex-col">
          <p className="text-lg font-semibold text-gray-800 mb-4">
            Overdue Grievances
          </p>
          <div className="overflow-x-auto overflow-y-auto h-[300px]">
            <table className="min-w-full text-sm text-left border border-gray-200 rounded-lg">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="py-3 px-4 border-b">S. No.</th>
                  <th className="py-3 px-4 border-b">Grievance ID</th>
                  <th
                    className="py-3 px-2 border-b cursor-pointer hover:bg-gray-200"
                    onClick={() => requestSort("source")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Source{" "}
                      <img src={upDownArrow} alt="sort" className="w-3 h-3" />
                    </div>
                  </th>
                  <th className="py-3 px-4 border-b">User Grievance</th>
                  <th className="py-3 px-4 border-b">Reg. Date</th>
                  <th className="py-3 px-4 border-b">Days Overdue</th>
                  <th className="py-3 px-4 border-b">Severity</th>
                </tr>
              </thead>
              <tbody>
                {stats.overdueGrievances.map((row, idx) => (
                  <tr
                    key={idx}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <td className="py-2 px-4">{idx + 1}</td>
                    <td
                      className="py-2 px-4 font-mono text-indigo-600 cursor-pointer"
                      onClick={() => handleViewClick(row)}
                    >
                      <TruncatedText text={row.grievance_id || ""} limit={20} />
                    </td>
                    <td className="py-2 px-4 text-gray-800">
                      <TruncatedText text={row.source || "CAG"} limit={20} />
                    </td>
                    <td className="py-2 px-4 text-gray-800">
                      <TruncatedText text={row.user_query || ""} limit={20} />
                    </td>
                    <td className="py-2 px-4 text-gray-600">
                      {new Date(
                        row.grievance_submission_timestamp,
                      ).toLocaleDateString("en-IN")}
                    </td>
                    <td className="py-2 px-4 text-gray-600">
                      {calculateDaysLapsed(row)}
                      {/* {new Date(row.grievance_submission_timestamp).toLocaleDateString('en-IN')} */}
                    </td>
                    <td
                      className={`py-2 px-4 font-medium ${
                        row.severity?.toLowerCase() === "critical" ||
                        row.severity?.toLowerCase() === "high"
                          ? "text-red-600"
                          : row.severity?.toLowerCase() === "semi-critical" ||
                              row.severity?.toLowerCase() === "medium"
                            ? "text-yellow-600"
                            : "text-gray-800"
                      }`}
                    >
                      {toSentenceCase(row.severity || "")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* </div> */}
        </div>

        {/* <div className="bg-white shadow rounded-lg p-6 w-full">
          <p className="font-semibold mb-2">Grievances by Category</p>
          {stats.categories.map((cat, idx) => {
            const percent = ((cat.value / stats.total) * 100).toFixed(0);
            return (
              <div key={idx} className="mb-3">
                <div className="flex justify-between text-sm">
                  <span>{cat.name}</span>
                  <span>{percent}%</span>
                </div>
                <div className="bg-gray-200 h-2 rounded mt-1">
                  <div className="h-2 bg-gradient-to-r from-[#FF852B] via-[#D37929] to-[#B34700] rounded" style={{ width: `${percent}%` }}></div>
                </div>
              </div>
            );
          })}
        </div> */}
      </div>

      {/* Grievance Table */}
      <div className="bg-white p-4 w-full">
        {/* <p className="font-semibold mb-2">Grievances Over Time</p>
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-gray-600 border-b">
              <th className="py-2">S. No.</th>
              <th className="py-2">Grievance ID</th>
              <th className="py-2">User Query</th>
              <th className="py-2">Category</th>
              <th className="py-2">Status</th>
              <th className="py-2">Date</th>
              <th className="py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {stats.allGrievances.map((row, idx) => (
              <tr key={idx} className="border-b hover:bg-gray-100 transition">
                {
                  console.log('row', row)
                }
                <td className="py-2">{idx + 1}</td>
                <td className="py-2">{row.grievance_id}</td>
                <td className="py-2">{row.user_query}</td>
                <td className="py-2">{`${row.llm_category}(${row.llm_subcategory})`}</td>
                <td className="py-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold
                    ${row.officer_grievance_status?.toLowerCase() === 'pending' ? 'bg-amber-100 text-amber-700' :
                      row.officer_grievance_status?.toLowerCase() === 'resolved' || "responded" ? 'bg-emerald-100 text-emerald-700' :
                        'bg-red-100 text-red-700'}
                  `}>
                    {row.status}
                  </span>
                </td>
                <td className="py-2">{row.grievance_submission_timestamp}</td>
                <td className="py-2">
                  <div className="flex justify-center items-center gap-2">
                    {row.officer_grievance_status?.toLowerCase() === "pending" ? (
                      <VisibilityIcon fontSize="small" className="text-[#FF852B] cursor-pointer" onClick={() => handleViewClick(row)} />
                    ) : (
                      <EditIcon fontSize="small" className="text-[#B34700] cursor-pointer" />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table> */}
        <div className="bg-white p-6 w-full mt-3">
          <p className="text-lg font-semibold text-gray-800 mb-4">
            Grievances Over Time
          </p>
          <div className="flex items-center gap-4 mb-4">
            <input
              type="text"
              placeholder={`Search by Grievance ID...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring focus:ring-indigo-200 w-full sm:w-72"
            />
          </div>
          <div className="overflow-x-auto overflow-y-auto rounded-lg h-[60vh]">
            <table className="min-w-full text-sm text-left border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="py-3 px-4 border-b">S. No.</th>
                  <th
                    className="py-3 px-4 border-b cursor-pointer hover:bg-gray-200"
                    onClick={() => requestSort("grievance_id")}
                  >
                    <div className="flex items-center gap-1">
                      Grievance ID{" "}
                      <img
                        src={upDownArrow}
                        alt="sort"
                        className="w-3 h-3 inline"
                      />
                    </div>
                  </th>
                  <th
                    className="py-3 px-2 border-b cursor-pointer hover:bg-gray-200"
                    onClick={() => requestSort("source")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Source{" "}
                      <img src={upDownArrow} alt="sort" className="w-3 h-3" />
                    </div>
                  </th>
                  <th className="py-3 px-4 border-b">User Grievance</th>
                  <th className="py-3 px-4 border-b">Department</th>
                  <th className="py-3 px-4 border-b">Status</th>
                  <th
                    className="py-3 px-4 border-b cursor-pointer hover:bg-gray-200"
                    onClick={() => requestSort("severity")}
                  >
                    <div className="flex items-center gap-1">
                      Severity{" "}
                      <img
                        src={upDownArrow}
                        alt="sort"
                        className="w-3 h-3 inline"
                      />
                    </div>
                  </th>
                  <th
                    className="py-3 px-4 border-b cursor-pointer hover:bg-gray-200"
                    onClick={() => requestSort("reg_date")}
                  >
                    <div className="flex items-center gap-1">
                      Reg. Date{" "}
                      <img
                        src={upDownArrow}
                        alt="sort"
                        className="w-3 h-3 inline"
                      />
                    </div>
                  </th>
                  <th
                    className="py-3 px-2 border-b cursor-pointer hover:bg-gray-200"
                    onClick={() => requestSort("days_lapsed")}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Days Overdue{" "}
                      <img src={upDownArrow} alt="sort" className="w-3 h-3" />
                    </div>
                  </th>
                  <th
                    className="py-3 px-4 border-b cursor-pointer hover:bg-gray-200"
                    onClick={() => requestSort("tat")}
                  >
                    <div className="flex items-center gap-1">
                      TAT{" "}
                      <img
                        src={upDownArrow}
                        alt="sort"
                        className="w-3 h-3 inline"
                      />
                    </div>
                  </th>
                  <th className="py-3 px-4 border-b text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredGrievances.map((row, idx) => (
                  <tr
                    key={idx}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <td className="py-2 px-4">{idx + 1}</td>
                    <td className="py-2 px-4 font-mono text-indigo-600">
                      <TruncatedText text={row.grievance_id} limit={6} />
                    </td>
                    <td className="py-2 px-4 text-gray-800">
                      <TruncatedText text={row.source || "CAG"} limit={20} />
                    </td>
                    <td className="py-2 px-4 text-gray-800">
                      <TruncatedText text={row.user_query} limit={20} />
                    </td>
                    <td className="py-2 px-4">
                      <TruncatedText
                        text={`${row.llm_category} (${row.llm_subcategory})`}
                        limit={20}
                      />
                    </td>
                    <td className="py-2 px-4">
                      <span
                        className={`min-w-[100px] inline-block text-center px-3 py-1 text-xs font-medium rounded-full
      ${
        row.officer_grievance_status?.toLowerCase() === "resolved"
          ? "bg-green-600 text-white" // dark yellow
          : "bg-yellow-600 text-white" // fallback
      }
    `}
                      >
                        {toSentenceCase(row.officer_grievance_status || "")}
                      </span>
                    </td>
                    <td
                      className={`py-2 px-4 font-medium ${
                        row.severity?.toLowerCase() === "critical" ||
                        row.severity?.toLowerCase() === "high"
                          ? "text-red-600"
                          : row.severity?.toLowerCase() === "semi-critical" ||
                              row.severity?.toLowerCase() === "medium"
                            ? "text-yellow-600"
                            : "text-gray-800"
                      }`}
                    >
                      <TruncatedText text={row.severity || ""} limit={20} />
                    </td>
                    <td className="py-2 px-4 text-gray-600">
                      {new Date(
                        row.grievance_submission_timestamp,
                      ).toLocaleDateString("en-IN")}
                    </td>
                    <td className="py-2 px-4 text-gray-600">
                      {calculateDaysLapsed(row)}
                      {/* {new Date(row.grievance_submission_timestamp).toLocaleDateString('en-IN')} */}
                    </td>
                    <td className="py-2 px-2 text-gray-800">
                      <TruncatedText text={row.tat || ""} limit={20} />
                    </td>

                    <td className="py-2 px-4 text-center">
                      <div className="flex justify-center items-center gap-2">
                        {row.officer_grievance_status?.toLowerCase() !==
                        "pending" ? (
                          <VisibilityIcon
                            fontSize="small"
                            className={`
    cursor-pointer
    ${
      row.officer_grievance_status?.toLowerCase() === "resolved"
        ? "text-green-600 hover:text-green-700"
        : "text-yellow-600 hover:text-yellow-700"
    }
  `}
                            onClick={() => handleViewClick(row)}
                          />
                        ) : (
                          <EditIcon
                            fontSize="small"
                            className={`
    cursor-pointer
    ${
      row.officer_grievance_status?.toLowerCase() === "pending"
        ? "text-yellow-600 hover:text-yellow-700"
        : row.officer_grievance_status?.toLowerCase() === "responded"
          ? "text-green-600 hover:text-green-700"
          : "text-black hover:text-gray-800"
    }
  `}
                            onClick={() => handleEditClick(row)}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        {isModalOpen && selectedRow && (
          <div className="fixed inset-0 flex items-start sm:items-center justify-center bg-black bg-opacity-50 p-4 sm:p-0">
            <div className="bg-white w-full sm:w-[80vw] max-w-4xl p-5 sm:p-6 rounded shadow-lg max-h-[85vh] sm:max-h-[90vh] overflow-auto mt-6 sm:mt-0">
              <h3 className="text-lg font-bold mb-4">Grievance Summary</h3>
              <form className="space-y-4">
                {/* Query Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    User Grievance
                  </label>
                  <input
                    type="text"
                    name="user_query"
                    value={selectedRow.user_query}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Bot Response
                  </label>
                  <textarea
                    name="llm_response_desc"
                    value={selectedRow.llm_response_desc}
                    // onChange={handleInputChange}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                    rows={3} // You can adjust the number of rows as per your requirement
                  ></textarea>
                </div>
                {selectedRow?.officer_transfer_comments?.trim() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Transfer Comment from Officer
                    </label>
                    <textarea
                      name="officer_grievance_comments"
                      value={
                        selectedRow?.officer_transfer_comments?.trim() || ""
                      }
                      readOnly={true}
                      className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                      rows={3} // You can adjust the number of rows as per your requirement
                    ></textarea>
                  </div>
                )}
                {/* Response from expert */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Comment from officer
                  </label>
                  <textarea
                    name="officer_grievance_comments"
                    value={
                      selectedRow?.officer_grievance_comments ||
                      selectedRow?.grievance_response
                    }
                    onChange={handleInputChange}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                    rows={3} // You can adjust the number of rows as per your requirement
                  ></textarea>
                </div>

                {/* <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Ministry
                  </label>
                  <input
                    type="text"
                    name="llm_ministry"
                    value={selectedRow.llm_ministry}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                  />
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Department
                  </label>
                  <input
                    type="text"
                    name="llm_category"
                    value={selectedRow.llm_category}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Sub Category
                  </label>
                  <input
                    type="text"
                    name="llm_subcategory"
                    value={selectedRow.llm_subcategory}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    State
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={selectedRow.state || ""}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    District
                  </label>
                  <input
                    type="text"
                    name="district"
                    value={selectedRow.district}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Severity
                  </label>
                  <input
                    type="text"
                    name="severity"
                    value={selectedRow.severity}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    User Id
                  </label>
                  <input
                    type="text"
                    name="user_id"
                    value={selectedRow.user_id}
                    readOnly={true}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                  />
                </div>

                {/* ATR Upload & Summary Section - only for responded grievances */}
                {(selectedRow.atr_uploaded ||
                  selectedRow?.officer_grievance_status?.toLowerCase() ===
                    "attach atr") && (
                  <div className="bg-gray-50 p-4 rounded-lg border">
                    <h4 className="text-md font-semibold text-gray-800 mb-2">
                      ATR Management
                    </h4>

                    {/* Upload Controls - only show when no summary exists yet */}
                    {!decodeSummary(selectedRow?.atr_summary).summary &&
                      !atrSummary && (
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 mb-4">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setAtrFile(e.target.files[0])}
                            className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-green-300 text-sm"
                          />
                          <button
                            type="button"
                            className="px-4 py-2 w-full sm:w-auto bg-green-500 text-white rounded hover:bg-green-600 font-medium disabled:opacity-50"
                            onClick={handleAtrUpload}
                            disabled={atrUploadLoading || !atrFile}
                          >
                            {atrUploadLoading ? "Uploading..." : "Upload ATR"}
                          </button>
                        </div>
                      )}

                    {/* Summary Display - show from fresh upload or from backend */}
                    {(atrSummary ||
                      decodeSummary(selectedRow?.atr_summary).summary) && (
                      <div className="mt-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ATR Summary
                        </label>
                        <div className="w-full px-3 py-3 border rounded-lg bg-white text-gray-700 text-sm max-h-48 overflow-y-auto whitespace-pre-wrap">
                          {atrSummary ||
                            decodeSummary(selectedRow?.atr_summary).summary}
                        </div>
                      </div>
                    )}

                    {/* Download ATR Button - only show after save (persisted in backend) */}
                    {!atrSummary &&
                      (selectedRow.atr_file_url ||
                        (!atrFileUrl &&
                          decodeSummary(selectedRow?.atr_summary).fileUrl)) && (
                        <div className="mt-4">
                          <button
                            type="button"
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 font-medium"
                            onClick={() => handleATR(selectedRow.grievance_id)}
                          >
                            Download ATR
                          </button>
                        </div>
                      )}
                  </div>
                )}

                {/* Audio Upload Section - only show after ATR summary is available */}
                {selectedRow?.officer_grievance_status?.toLowerCase() ===
                  "resolved" &&
                  (atrSummary ||
                    decodeSummary(selectedRow?.atr_summary).summary) && (
                    <div className="bg-gray-50 p-4 rounded-lg border mt-4">
                      <h4 className="text-md font-semibold text-gray-800 mb-2">
                        Audio Upload
                      </h4>
                      {!(
                        selectedRow.feedback_call_uploaded &&
                        selectedRow.feedback_call_summary
                      ) && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Audio File
                          </label>
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                            <input
                              type="file"
                              accept="audio/*"
                              onChange={(e) => setAudioFile(e.target.files[0])}
                              className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-green-300 text-sm"
                            />
                            <button
                              type="button"
                              className="px-4 py-2 w-full sm:w-auto bg-green-500 text-white rounded hover:bg-green-600 font-medium disabled:opacity-50"
                              onClick={handleAudioUpload}
                              disabled={audioUploadLoading || !audioFile}
                            >
                              {audioUploadLoading
                                ? "Uploading..."
                                : "Upload Audio"}
                            </button>
                          </div>
                        </div>
                      )}
                      {(audioSummary ||
                        decodeSummary(selectedRow?.atr_summary).summary) && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Audio Summary
                          </label>
                          <div className="w-full px-3 py-3 border rounded-lg bg-white text-gray-700 text-sm max-h-48 overflow-y-auto whitespace-pre-wrap">
                            {audioSummary ||
                              decodeSummary(selectedRow?.feedback_call_summary)
                                .summary}
                          </div>
                        </div>
                      )}
                      {/* {audioFileUrl && (
                      <div className="mt-4">
                        <button
                          type="button"
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 font-medium"
                          onClick={handleAudioDownload}
                        >
                          Download Audio
                        </button>
                      </div>
                    )} */}
                    </div>
                  )}

                {/* Buttons */}
                <div className="flex flex-wrap justify-end gap-2 sm:gap-4 pt-4 border-t">
                  {selectedRow?.officer_grievance_status === "Add Comment" && (
                    <>
                      <button
                        type="button"
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 whitespace-nowrap flex-1 sm:flex-none text-center"
                        onClick={handleTransferAdmin}
                      >
                        Raise to Admin
                      </button>

                      <button
                        type="button"
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 whitespace-nowrap flex-1 sm:flex-none text-center"
                        onClick={handleTransfer}
                      >
                        Transfer
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 whitespace-nowrap flex-1 sm:flex-none text-center"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                  {/* Show Save for: pending grievances OR responded with freshly uploaded ATR OR freshly uploaded Audio */}
                  {(selectedRow?.officer_grievance_status?.toLowerCase() !==
                    "resolved" ||
                    atrSummary ||
                    audioSummary) && (
                    <button
                      type="button"
                      className="px-4 py-2 bg-[#45B07B] text-white rounded hover:bg-[#12A57C] whitespace-nowrap flex-1 sm:flex-none text-center"
                      onClick={handleSave}
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

        {transfer && selectedRow && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 sm:p-0">
            <div className="bg-white w-full sm:w-[50vw] max-w-lg p-5 sm:p-6 rounded shadow-lg max-h-[85vh] sm:max-h-[90vh] overflow-auto">
              <h3 className="text-lg font-bold mb-4">Transfer Grievance</h3>
              <form className="space-y-4" onSubmit={submitTransfer}>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Transfer to
                  </label>
                  <select
                    required
                    onChange={setTransferDeptartment}
                    className={`w-full px-3 py-2 border  "border-gray-300"
                       rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-300`}
                  >
                    <option value="">Select Department</option>
                    {!(selectedRow?.llm_category || user?.category || "")
                      .replace(/\s+/g, "")
                      .toLowerCase()
                      .includes("ia&ad") && (
                      <option value="Employee/Pensioner of IA&AD">
                        Employee/Pensioner of IA&AD
                      </option>
                    )}
                    {!(selectedRow?.llm_category || user?.category || "")
                      .replace(/\s+/g, "")
                      .toLowerCase()
                      .includes("stategovt") && (
                      <option value="Grievance pertaining to State Govt. Employees/ Pensioners if pending in PAGs/AGs offices">
                        Grievance pertaining to State Govt. Employees/
                        Pensioners if pending in PAGs/AGs offices
                      </option>
                    )}
                    {/* <option value="Admin">Admin</option> */}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Comment from officer
                  </label>
                  <textarea
                    required
                    name="officer_comments"
                    value={officerComments}
                    onChange={handleOfficeComment}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                    rows={3} // You can adjust the number of rows as per your requirement
                  ></textarea>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-4 pt-4 border-t">
                  <button
                    type="button"
                    className="px-4 py-2 w-full sm:w-auto bg-gray-500 text-white rounded hover:bg-gray-600 text-center"
                    onClick={closeTransfer}
                  >
                    Cancel
                  </button>
                  {/* Show Save for: pending grievances OR responded with freshly uploaded ATR OR freshly uploaded Audio */}

                  <button
                    type="submit"
                    className="px-4 py-2 w-full sm:w-auto bg-blue-500 text-white rounded hover:bg-blue-600 text-center"
                  >
                    {loading ? "Transferring..." : "Transfer"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {transferAdmin && selectedRow && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4 sm:p-0">
            <div className="bg-white w-full sm:w-[50vw] max-w-lg p-5 sm:p-6 rounded shadow-lg max-h-[85vh] sm:max-h-[90vh] overflow-auto">
              <h3 className="text-lg font-bold mb-4">
                Raise Grievance to Admin
              </h3>
              <form className="space-y-4" onSubmit={submitTransfer}>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Comment from officer
                  </label>
                  <textarea
                    required
                    name="officer_comments"
                    value={officerComments}
                    onChange={handleOfficeComment}
                    className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                    rows={3} // You can adjust the number of rows as per your requirement
                  ></textarea>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-4 pt-4 border-t">
                  <button
                    type="button"
                    className="px-4 py-2 w-full sm:w-auto bg-gray-500 text-white rounded hover:bg-gray-600 text-center"
                    onClick={closeTransfer}
                  >
                    Cancel
                  </button>
                  {/* Show Save for: pending grievances OR responded with freshly uploaded ATR OR freshly uploaded Audio */}

                  <button
                    type="submit"
                    className="px-4 py-2 w-full sm:w-auto bg-blue-500 text-white rounded hover:bg-blue-600 text-center"
                  >
                    {loading ? "Raising..." : "Raise"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
